import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { apiClient } from 'lib/api';
import type { QueryConfig } from 'lib/react-query';
import { DEV_MODE } from '../constants';
import { filterMockTwitterFlows } from './mockTwitterCreativeSettings';
import queryKeys from './queryKeys';
import type { GetTwitterFlowsParams, TwitterFlow } from './types';

const MOCK_DELAY_MS = 200;

/** Flows of X ordered by name (then app); `[]` when nothing matches. */
export const getTwitterFlows = async (
  { params }: { params: GetTwitterFlowsParams },
  signal?: AbortSignal,
): Promise<TwitterFlow[]> => {
  // PROTOTYPE ONLY: mock branch, active while DEV_MODE is true.
  if (DEV_MODE) {
    await new Promise((resolve) => setTimeout(resolve, MOCK_DELAY_MS));
    return filterMockTwitterFlows(params);
  }

  const res = await apiClient.get<TwitterFlow[]>(queryKeys.flows, { params, signal });

  if (isAxiosError(res)) {
    const error = new Error('Get X Flows Failed');
    error.cause = res.response?.data;
    return Promise.reject(error);
  }

  return res.data;
};

interface UseTwitterFlowsOptions<TData> {
  params: GetTwitterFlowsParams;
  config?: QueryConfig<typeof getTwitterFlows, TData>;
}

export const useTwitterFlows = <TData = TwitterFlow[]>({ params, config }: UseTwitterFlowsOptions<TData>) =>
  useQuery({
    staleTime: 30 * 60 * 1000,
    ...config,
    queryFn: ({ signal }) => getTwitterFlows({ params }, signal),
    queryKey: [queryKeys.flows, params],
  });
